import { generatePath, To } from 'react-router-dom'

import { FilterValue } from 'components/Filter'
import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { QueryProps } from 'features/inventory/domain/components/DomainsTable/types/api'
import {
  DomainsForTable,
  DomainSortBy,
} from 'features/inventory/domain/components/DomainsTable/types/model'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { calculatePagination } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { Alert, useGetAlerts } from './hooks/useGetAlerts'
import { useGetDomains } from './hooks/useGetDomains'
import { useColumns } from './useColumns'

export type TableProps = {
  filters: FilterValue[]
}

export const DomainsTable = ({ filters }: TableProps): JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { page, rowsPerPage, setPage, ...usePaginationProps } = usePagination({
    resetOnChange: [filters],
  })
  const { props: sortProps, sort } = useSort<DomainsForTable, DomainSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const queryVariables: QueryProps = {
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  }
  const { count, domains, loading } = useGetDomains(queryVariables)
  const alerts: Alert[] = useGetAlerts(queryVariables)

  const columns = useColumns({ alerts, count, filters })

  const handleRowClick = (row: DomainsForTable): To =>
    generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
      id: row.id,
      tab: 'general',
      workspaceId,
    })

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    ...usePaginationProps,
  }

  const canAccessDomainEdit: boolean = ability.can('access', 'DomainPage-edit')

  return (
    <Table<DomainsForTable>
      columns={columns}
      data={domains}
      loading={loading}
      meta={{ stickyHeader: true }}
      onClickRow={canAccessDomainEdit ? id => handleRowClick(id) : undefined}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
